<!-- GlideByStrokeChart — per-stroke Glide (SWOLF) proportional bars.
     Extracted from P6Swolf; silk/rough lists and proto chrome stripped.
     All text raised to 15px (P6 used ~12-13px). -->
<script lang="ts">
  import type { DerivedWorkout } from "../../lib/lapAnalysis";
  import { strokeColor } from "../../lib/designTokens";
  import StrokePill from "../../lib/StrokePill.svelte";

  interface Props { dw: DerivedWorkout }
  let { dw }: Props = $props();

  // Scale the bars by the largest avg-strokes value on screen (the raw fact).
  let maxStrokes = $derived(
    Math.max(0, ...(dw.swolfStats?.perStrokeAvg.map((p) => p.avgStrokes) ?? [])),
  );

  // Canonical stroke order — the bars are a picture of the swim, not a
  // leaderboard, so they read in a stable order regardless of average.
  const CANONICAL_ORDER = ["Freestyle", "Backstroke", "Breaststroke", "Butterfly"];
  // Below this many lengths a stroke's average is too thin to rank confidently;
  // the row still shows, dimmed, so it never reads as a ranked result.
  const LENGTH_FLOOR = 4;
  let sortedRows = $derived(
    [...(dw.swolfStats?.perStrokeAvg ?? [])].sort((a, b) => {
      const ai = CANONICAL_ORDER.indexOf(a.stroke);
      const bi = CANONICAL_ORDER.indexOf(b.stroke);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    }),
  );
</script>

{#if !dw.swolfStats || dw.swolfStats.perStrokeAvg.length === 0}
  <p class="no-data">No stroke data in this swim.</p>
{:else}
  <div class="per-stroke">
    {#each sortedRows as p (p.stroke)}
      <div class="ps-row" class:ps-low={p.lapCount < LENGTH_FLOOR}>
        <span class="ps-label">
          <StrokePill stroke={p.stroke} />
          <span class="ps-name">{p.stroke}</span>
        </span>
        <span class="ps-count">{p.lapCount}×</span>
        <span class="ps-bar-wrap">
          {#if maxStrokes > 0}
            <span
              class="ps-bar"
              style:width="{(p.avgStrokes / maxStrokes) * 100}%"
              style:background={strokeColor(p.stroke)}
            ></span>
          {/if}
        </span>
        <span class="ps-value">{p.avgStrokes.toFixed(0)} str · {p.avgSeconds.toFixed(0)}s</span>
      </div>
    {/each}
  </div>
{/if}

<style>
  .no-data {
    font-size: 15px;
    color: var(--text-muted);
    text-align: center;
    padding: 24px 0;
    margin: 0;
  }

  .per-stroke {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
  }

  /* All text raised to 15px in this card context. Label column is fixed-width
     so bars/values align across rows, sized to fit the longest stroke name in
     CANONICAL_ORDER ("Breaststroke", 12 chars) plus its pill so no stroke
     name ever mid-word truncates. */
  .ps-row {
    display: grid;
    grid-template-columns: 9.5rem auto 2fr max-content;
    gap: 0.4rem;
    align-items: center;
    font-size: 15px;
  }

  .ps-label {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
  }

  .ps-name { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

  .ps-low { opacity: 0.55; }

  .ps-count {
    font-size: 15px;
    color: var(--text-muted);
  }

  .ps-bar-wrap {
    height: 8px;
    background: var(--bg-canvas);
    border-radius: 4px;
    overflow: hidden;
  }

  .ps-bar {
    display: block;
    height: 100%;
    opacity: 0.85;
  }

  .ps-value {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    min-width: 32px;
    text-align: right;
    font-size: 15px;
  }
</style>
